<template>
  <div class="m-detail">
    <div v-if="loading" class="m-hint">{{ $t("mobile.loading") }}</div>

    <template v-else-if="ret">
      <section class="m-block">
        <div class="m-detail__headrow">
          <h2 class="m-heading">#{{ ret.id }} · {{ $t("returns.types." + ret.returnType) }}</h2>
          <span class="m-detail__status">
            {{ $t("returns.statuses." + ret.status) }}<template v-if="ret.completedOn"> · {{ $t("returns.generalStatus.completed") }}</template>
          </span>
        </div>
        <dl class="m-rows">
          <!-- Rechnungsnummer prominent, bewusst ohne Afterbuy-Link (nur Desktop) -->
          <template v-if="ret.invoiceNumber">
            <dt>{{ $t("returns.detail.invoice") }}</dt>
            <dd class="m-detail__invoice">{{ ret.invoiceNumber }}</dd>
          </template>
          <template v-if="ret.referenceNumber">
            <dt>{{ $t("returns.detail.reference") }}</dt>
            <dd>{{ ret.referenceNumber }}</dd>
          </template>
          <template v-if="ret.returnTrackingNumber">
            <dt>{{ $t("returns.detail.returnTracking") }}</dt>
            <dd>{{ ret.returnTrackingNumber }}</dd>
          </template>
          <template v-if="ret.platformOrderId">
            <dt>{{ $t("returns.table.platformOrder") }}</dt>
            <dd>{{ ret.platformOrderId }}</dd>
          </template>
          <template v-if="ret.secondaryOrderId">
            <dt>{{ $t("returns.table.shopOrder") }}</dt>
            <dd>{{ ret.secondaryOrderId }}</dd>
          </template>
          <template v-if="carrier">
            <dt>{{ $t("returns.table.carrier") }}</dt>
            <dd>
              <span class="m-mp" :style="{ background: carrier.color, color: carrier.text }">{{ carrier.short }}</span> {{ carrier.label }}
            </dd>
          </template>
          <template v-if="marketplace || ret.channel || ret.afterbuyPlatform">
            <dt>{{ $t("returns.detail.channel") }}</dt>
            <dd>
              <span v-if="marketplace" class="m-mp" :style="{ background: marketplace.color }">{{ marketplace.short }}</span>
              {{ marketplace ? marketplace.label : ret.channel || ret.afterbuyPlatform }}
            </dd>
          </template>
          <template v-if="ret.senderName">
            <dt>{{ $t("returns.detail.sender") }}</dt>
            <dd>{{ ret.senderName }}<br v-if="ret.senderStreet" />{{ ret.senderStreet }}<br v-if="ret.senderZipcode || ret.senderTown" />{{ ret.senderZipcode }} {{ ret.senderTown }}</dd>
          </template>
          <template v-if="ret.customerOrderId">
            <dt>{{ $t("returns.summary.order") }}</dt>
            <dd>{{ ret.customerOrderId }}</dd>
          </template>
          <template v-if="ret.createdBy">
            <dt>{{ $t("returns.detail.createdBy") }}</dt>
            <dd>{{ ret.createdBy }} · {{ ret.createdOn | formatDateTimeSeconds }}</dd>
          </template>
          <template v-if="ret.completedOn">
            <dt>{{ $t("returns.detail.completedBy") }}</dt>
            <dd>{{ ret.completedBy }} · {{ ret.completedOn | formatDateTimeSeconds }}</dd>
          </template>
        </dl>
        <p v-if="ret.afterbuyMemo" class="m-detail__memo">{{ ret.afterbuyMemo }}</p>
      </section>

      <!-- Notiz: auch das Lager kann hier Infos ergaenzen (Kundenwunsch 04.08.) -->
      <section class="m-block">
        <h3 class="m-heading">{{ $t("returns.detail.notes") }}</h3>
        <textarea v-model="notesDraft" class="m-detail__notes" rows="3" :placeholder="$t('returns.detail.notesHint')"></textarea>
        <button class="m-detail__secondary" :disabled="savingNotes || notesDraft === (ret.notes || '')" @click="saveNotes">
          {{ savingNotes ? $t("mobile.loading") : $t("returns.detail.notesSave") }}
        </button>
      </section>

      <section v-if="ret.items && ret.items.length" class="m-block">
        <h3 class="m-heading">{{ $t("returns.items.title") }}</h3>
        <ul class="m-detail__items">
          <li v-for="i in ret.items" :key="i.id">
            <span>{{ i.quantity }}× {{ i.sku }}</span>
            <span class="m-detail__cond">{{ conditionLabel(i.condition) }}</span>
            <span v-if="i.warehouseInventoryId" class="m-detail__loc">
              <i class="material-icons ok">check_circle</i>
              {{ i.storageLocation }}
            </span>
          </li>
        </ul>
      </section>

      <section class="m-block">
        <h3 class="m-heading">{{ $t("returns.detail.photos") }} ({{ photoList.length }})</h3>
        <div class="m-detail__photos">
          <figure v-for="p in photoList" :key="p.id" class="m-detail__photo">
            <img v-if="p.url" :src="p.url" alt="" @click="openViewer(p.id)" />
            <div v-else class="m-detail__photo-na"><i class="material-icons">block</i></div>
            <figcaption>{{ photoTypeLabel(p.photoTypeCode) }}</figcaption>
          </figure>
        </div>
      </section>

      <!-- "Erledigt setzen" gibt es bewusst nur am Desktop: das macht das Buero, nicht das Lager -->
      <!-- Wiedereinstieg bis zur FINALEN Buchung (Kundenwunsch 25.08.): auch eine abgeschlossene
           Erfassung (zugeordnet/geprueft) muss noch bearbeitet und eingebucht werden koennen -->
      <button v-if="ret.status !== 'booked'" class="m-detail__primary" @click="$router.push(`/m/retoure/neu?id=${ret.id}`)">
        {{ $t("returns.continueDraft") }}
      </button>
    </template>

    <!-- Grossansicht mit X und Blaettern (Kundenwunsch 18.08., P17.1) -->
    <div v-if="viewerIndex !== null" class="m-detail__zoom" @click.self="viewerIndex = null">
      <button class="m-detail__zoombtn m-detail__zoombtn--close" @click="viewerIndex = null"><i class="material-icons">close</i></button>
      <button v-if="viewablePhotos.length > 1" class="m-detail__zoombtn m-detail__zoombtn--prev" @click.stop="stepViewer(-1)"><i class="material-icons">chevron_left</i></button>
      <img :src="viewablePhotos[viewerIndex].url" alt="" />
      <button v-if="viewablePhotos.length > 1" class="m-detail__zoombtn m-detail__zoombtn--next" @click.stop="stepViewer(1)"><i class="material-icons">chevron_right</i></button>
    </div>
  </div>
</template>

<script>
import { marketplaceInfo, carrierInfo } from "@/helpers/marketplaces";
import ReturnsService from "@/services/returns.service";

export default {
  name: "MobileReturnDetail",
  data() {
    return {
      ret: null,
      photoList: [],
      loading: true,
      viewerIndex: null,
      notesDraft: "",
      savingNotes: false,
    };
  },
  async created() {
    try {
      this.ret = await ReturnsService.get(this.$route.params.id);
      this.notesDraft = this.ret.notes || "";
      const photos = this.ret.photos || [];
      this.photoList = photos.map((p) => ({ ...p, url: null }));
      // Thumbnails nacheinander laden (Blob-URLs, wie die Dokumentvorschau am Desktop)
      for (const p of this.photoList) {
        if (!p.isNotAvailable) {
          p.url = await ReturnsService.photoContentUrl(this.ret.id, p.id).catch(() => null);
          this.photoList = [...this.photoList];
        }
      }
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    this.photoList.forEach((p) => p.url && URL.revokeObjectURL(p.url));
  },
  computed: {
    marketplace() {
      return marketplaceInfo(this.ret && this.ret.marketplace);
    },
    carrier() {
      return carrierInfo(this.ret && this.ret.carrier);
    },
    viewablePhotos() {
      return this.photoList.filter((p) => p.url);
    },
  },
  methods: {
    openViewer(photoId) {
      const idx = this.viewablePhotos.findIndex((p) => p.id === photoId);
      if (idx >= 0) this.viewerIndex = idx;
    },
    stepViewer(delta) {
      const n = this.viewablePhotos.length;
      if (!n) return;
      this.viewerIndex = (this.viewerIndex + delta + n) % n;
    },
    photoTypeLabel(code) {
      const key = "returns.photoTypes." + code;
      return this.$te(key) ? this.$t(key) : code;
    },
    conditionLabel(c) {
      if (c === "B") return this.$t("returns.items.conditionB");
      if (c === "S") return this.$t("returns.items.conditionS");
      if (c === "U") return this.$t("returns.items.conditionU");
      return this.$t("returns.items.conditionA");
    },
    async saveNotes() {
      this.savingNotes = true;
      try {
        await ReturnsService.update(this.ret.id, { notes: this.notesDraft });
        this.ret.notes = this.notesDraft;
      } finally {
        this.savingNotes = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.m-mp {
  display: inline-block;
  min-width: 30px;
  padding: 1px 5px;
  border-radius: 4px;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  margin-right: 4px;
}
.m-detail {
  padding: 12px 12px 24px;
}

.m-block {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  padding: 14px;
  margin-bottom: 12px;
}

.m-heading {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 10px;
}

.m-detail__headrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.m-detail__status {
  font-size: 12px;
  color: #616161;
  background: #eeeeee;
  border-radius: 8px;
  padding: 2px 8px;
}

.m-rows {
  margin: 0;

  dt {
    font-size: 12px;
    color: #9e9e9e;
    margin-top: 8px;
  }
  dd {
    margin: 0;
    font-size: 14px;
  }
}

.m-detail__memo {
  font-size: 12px;
  color: #6d4c41;
  background: #fff3e0;
  border-radius: 8px;
  padding: 8px 10px;
  margin: 10px 0 0;
  white-space: pre-line;
}

.m-detail__items {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 14px;

    &:last-child {
      border-bottom: 0;
    }
    i.ok {
      color: #7f915a;
      font-size: 18px;
      margin-left: auto;
    }
  }
}

.m-detail__cond {
  font-size: 11px;
  background: #e3f2fd;
  color: #1565c0;
  border-radius: 8px;
  padding: 1px 7px;
}

.m-detail__invoice {
  font-weight: 600;
  font-size: 16px !important;
}

.m-detail__loc {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  font-size: 12px;
  color: #616161;

  i.ok {
    color: #7f915a;
    font-size: 18px;
    margin-left: 0 !important;
  }
}

.m-detail__notes {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.m-detail__secondary {
  display: block;
  width: 100%;
  margin-top: 8px;
  background: #fff;
  color: #7f915a;
  border: 1px solid #7f915a;
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    color: #bdbdbd;
    border-color: #e0e0e0;
  }
}

.m-detail__complete {
  display: block;
  width: 100%;
  margin-top: 10px;
  background: #673ab7;
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 13px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
}

.m-detail__photos {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.m-detail__photo {
  margin: 0;

  img {
    width: 100%;
    height: 90px;
    object-fit: cover;
    border-radius: 8px;
    display: block;
  }

  figcaption {
    font-size: 11px;
    color: #757575;
    padding-top: 2px;
    text-align: center;
  }
}

.m-detail__photo-na {
  height: 90px;
  border-radius: 8px;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bdbdbd;
}

.m-detail__primary {
  display: block;
  width: 100%;
  background: #7f915a;
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 13px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
}

.m-detail__zoom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: calc(100vw - 16px);
    max-height: calc(100vh - 90px);
    object-fit: contain;
  }

  .m-detail__zoombtn {
    position: absolute;
    border: none;
    border-radius: 50%;
    width: 42px;
    height: 42px;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;

    &--close {
      top: 12px;
      right: 12px;
    }
    &--prev {
      left: 8px;
      top: 50%;
      transform: translateY(-50%);
    }
    &--next {
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
    }
  }
}

.m-hint {
  text-align: center;
  color: #9e9e9e;
  padding: 18px 0;
}
</style>
